import { ArrowRight } from "lucide-react";
import {
  useOrgRoles,
  useOrgUnits,
  useUsersByRefs,
} from "@/hooks/useMasterData";
import { ApprovalNodeType, ApproverType } from "@/types/flow";
import { User } from "@/types/domain";
import { Avatar } from "@ui/avatar";
import { cn } from "@/utils/cn";
import { ReactNode } from "react";
import { ExpressionIcon } from "@/components/icons";

type ApproverTagProps = {
  data: ApprovalNodeType["data"];
};

const Tag = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "bg-green-light-6 text-green rounded-xs h-6 px-2 flex items-center text-xs font-medium whitespace-nowrap",
      className,
    )}
  >
    <span className="overflow-ellipsis overflow-hidden max-w-full whitespace-nowrap">
      {children}
    </span>
  </div>
);

export const ApproverTag = ({ data }: ApproverTagProps) => {
  const { units } = useOrgUnits();
  const { roles } = useOrgRoles();
  const su = data.specificUser as
    | { userId?: string; userIds?: string[]; users?: User[] }
    | undefined;
  const userIds = su?.userIds ?? (su?.userId ? [su.userId] : []);
  const { users } = useUsersByRefs(userIds);
  const resolvedUsers = userIds.length
    ? userIds
        .map(
          (id) =>
            users.find((user) => user.code === id || user.id === id) ??
            su?.users?.find((user) => user.code === id || user.id === id),
        )
        .filter((user): user is User => user !== undefined)
    : (su?.users ?? []);

  const renderReportingLineTag = () => {
    if (
      data.approver !== ApproverType.ApplicantReportLine &&
      data.approver !== ApproverType.UserReportLine
    ) {
      return null;
    }

    const method = data.approveMethod;
    if (!method) return null;

    if ("jobGrade" in method && method.jobGrade !== undefined) {
      return (
        <div className="flex flex-row gap-2 items-center justify-center pl-1.5">
          <ArrowRight className="text-primary-text w-4 h-4" />
          <Tag>L{method.jobGrade}</Tag>
        </div>
      );
    }

    if ("approveLevel" in method && method.approveLevel !== undefined) {
      return (
        <div className="flex flex-row gap-2 items-center justify-center text-primary-text pl-1.5">
          <div>&</div>
          <Tag>{method.approveLevel}L</Tag>
        </div>
      );
    }

    return null;
  };

  const renderDepartmentTag = () => {
    if (data.approver !== ApproverType.DepartmentSupervisor) return null;

    if (data.departmentSupervisor?.type === "reference") {
      return (
        <Tag className="ml-1.5">
          <ExpressionIcon className="w-3 h-3" />
        </Tag>
      );
    }

    const manualDepartment =
      data.departmentSupervisor?.type === "manual"
        ? data.departmentSupervisor
        : undefined;
    const department =
      manualDepartment?.department?.name ??
      units?.find(
        (u) =>
          u.id === manualDepartment?.departmentId ||
          u.code === manualDepartment?.departmentId,
      )?.name;

    if (!department) return null;

    return <Tag className="ml-1.5">{department}</Tag>;
  };

  const renderRoleTag = () => {
    if (data.approver !== ApproverType.Role) return null;

    const roleData = data.specificRole;
    if (!roleData) return null;

    if (roleData.type === "reference") {
      return (
        <Tag className="ml-1.5">
          <ExpressionIcon className="w-3 h-3" />
        </Tag>
      );
    }

    // Backward compat: support both roleIds (multi) and roleId (single)
    const roleIds = roleData.roleIds?.length
      ? roleData.roleIds
      : roleData.roleId
        ? [roleData.roleId]
        : [];
    const resolvedRoles = roleData.roles?.length
      ? roleData.roles
      : roleIds
          .map((id) =>
            roleData.role?.id === id || roleData.role?.code === id
              ? roleData.role
              : roles?.find((r) => r.id === id || r.code === id),
          )
          .filter(Boolean);

    if (resolvedRoles.length === 0) return null;

    return (
      <div className="ml-1.5 flex gap-1 items-center min-w-0 overflow-hidden">
        <Tag className="max-w-[80px] truncate">{resolvedRoles[0]?.name}</Tag>
        {resolvedRoles.length > 1 && (
          <Tag className="shrink-0">+{resolvedRoles.length - 1}</Tag>
        )}
      </div>
    );
  };

  const renderUserAvatar = () => {
    if (data.approver !== ApproverType.User) return null;

    if (resolvedUsers.length === 0) return null;

    return (
      <div className="ml-1.5 flex -space-x-2 items-center">
        {resolvedUsers.slice(0, 3).map((u, i) => (
          <Avatar
            key={u.id}
            name={u.name}
            size="xs"
            className="ring-2 ring-white relative"
            style={{ zIndex: 3 - i }}
          />
        ))}
        {resolvedUsers.length > 3 && (
          <div className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 ring-2 ring-white text-[10px] font-medium text-gray-600 relative z-0 ml-2.5">
            +{resolvedUsers.length - 3}
          </div>
        )}
      </div>
    );
  };

  return (
    renderReportingLineTag() ??
    renderDepartmentTag() ??
    renderRoleTag() ??
    renderUserAvatar()
  );
};
